/**
 * Turning a dragged or pasted image into something a session's CLI can read.
 *
 * The browser hands over bytes; the CLI on the far side takes a *path*. Nothing
 * in between can shorten that: the terminal's input channel carries keystrokes,
 * and both CLIs' own paste readers read the clipboard of the machine they run
 * on — which, for a session driven through this console, is not the machine the
 * operator is sitting at. So the bytes are uploaded, the server writes them into
 * the session's workspace on its own host, and the path comes back to be typed.
 *
 * The pure parts of that live here so they can be tested without a DataTransfer,
 * a socket or a server.
 */

import { ApiError } from '@/api/client'
import type { MessageKey } from '@/i18n'

/** What both provider CLIs can actually read back. Kept in step with
 *  attachmentTypes in internal/api/handlers_attachments.go — a type accepted
 *  here and refused there is a round trip wasted on a message the operator could
 *  have had immediately. */
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])

/** Mirrors terminal.MaxAttachmentBytes. Checked here as well so a 50 MB file is
 *  refused before it is uploaded, rather than after. */
export const MAX_ATTACHMENT_BYTES = 12 << 20

/** How many files one gesture may carry. A drop is a deliberate act with a
 *  handful of files in it; a hundred is a slip, and each one costs an upload and
 *  a line at the prompt. */
const MAX_FILES_PER_DROP = 8

/** True when a drag carries files at all, as opposed to text selected inside
 *  the terminal or dragged in from another part of the page. */
export function carriesFiles(dt: Pick<DataTransfer, 'types'> | null | undefined): boolean {
  if (!dt) return false
  // `types` is a DOMStringList in older engines and an array elsewhere; both
  // answer to Array.from.
  return Array.from(dt.types ?? []).includes('Files')
}

/** The image files in a drop or a paste, in the order the operator gave them. */
export function imagesFrom(dt: DataTransfer | null | undefined): File[] {
  if (!dt?.files) return []
  return Array.from(dt.files)
    .filter((f) => IMAGE_TYPES.has(f.type))
    .slice(0, MAX_FILES_PER_DROP)
}

/** True when a file is an image this console will try to upload. */
export function isSupportedImage(file: File): boolean {
  return IMAGE_TYPES.has(file.type)
}

/**
 * What gets typed into the session once the file has landed.
 *
 * A trailing space and no newline, deliberately. The path is an ingredient, not
 * a command: the operator writes the sentence around it and presses Enter
 * themselves. Submitting for them would send a half-composed prompt to an agent
 * that is about to act on it.
 *
 * The two CLIs then do different things with it, and both work. Claude reads the
 * path out of the prompt when it answers; Codex consumes it as it is typed and
 * replaces it with an "[Image #1]" chip, which is why it must arrive as a single
 * write — it does, because sendInput puts one frame on the wire — and why the
 * file has to still exist when the operator presses Enter, since the chip
 * carries the path rather than the bytes.
 */
export function promptText(path: string): string {
  // Quoted only when it has to be. Kimene controls the file name rigorously but
  // not the directory — that is the operator's checkout — and a workspace path
  // with a space in it splits into two words in either composer, so the image is
  // never attached and the prompt gains half a path. Verified against Codex,
  // which accepts a single-quoted path and turns it into the same [Image #1]
  // chip. Paths without whitespace are left exactly as they were, which is every
  // path either CLI has been seen to take.
  //
  // A path containing a single quote is left bare: quoting it would need the
  // composer's own escaping rules, which are not a shell's, and guessing at them
  // would turn a rare failure into a silent wrong-file.
  const needsQuoting = /\s/.test(path) && !path.includes("'")
  return needsQuoting ? `'${path}' ` : `${path} `
}

/**
 * The message to show when an upload fails.
 *
 * Returns an i18n key and its parameters rather than a sentence, so the pane
 * stays translatable. The server's own codes are honoured where it has a better
 * answer than "something went wrong": it alone knows whether the lease is held.
 */
export function uploadFailure(err: unknown): { key: MessageKey; params?: Record<string, string> } {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'blocked':
        return { key: 'terminal.dropNeedsControl' }
      case 'payload_too_large':
        return { key: 'terminal.dropTooLarge' }
      case 'bad_request':
        return { key: 'terminal.dropNotImage' }
    }
    return { key: 'terminal.dropFailed', params: { reason: err.message } }
  }
  return { key: 'terminal.dropFailed', params: { reason: String(err) } }
}
